import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { 
  LayoutDashboard, Key, BarChart3, 
  Settings, Menu, X, Shield 
} from 'lucide-react';

const DashboardLayout = () => {
  const { user } = useUser();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Overview', href: '/dashboard', icon: LayoutDashboard },
    { name: 'API Keys', href: '/dashboard/keys', icon: Key },
    { name: 'Playground', href: '/dashboard/playground', icon: Shield },
    { name: 'Analytics & Logs', href: '/dashboard/analytics', icon: BarChart3 },
    { name: 'Settings', href: '/dashboard/settings', icon: Settings },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950 pt-16">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-16 left-0 bottom-0 w-64 bg-white dark:bg-dark-900 border-r border-gray-200 dark:border-dark-800
        transform transition-transform duration-300 z-40
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="p-6">
          {/* User Info */}
          <div className="mb-8 pb-6 border-b border-gray-200 dark:border-dark-800">
            <div className="flex items-center space-x-3">
              <img 
                src={user?.imageUrl} 
                alt={user?.fullName}
                className="w-10 h-10 rounded-full"
              />
              <div>
                <div className="font-medium">{user?.fullName}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {user?.primaryEmailAddress?.emailAddress}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              // Define animation classes for each icon
              const getIconAnimation = (name) => {
                switch(name) {
                  case 'Settings':
                    return 'group-hover:rotate-90 transition-transform duration-500';
                  case 'Playground':
                    return 'group-hover:scale-110 group-hover:rotate-12 transition-all duration-300';
                  case 'API Keys':
                    return 'group-hover:scale-110 group-hover:-rotate-12 transition-all duration-300';
                  case 'Analytics & Logs':
                    return 'group-hover:scale-110 transition-all duration-300';
                  case 'Overview':
                    return 'group-hover:scale-110 transition-all duration-300';
                  default:
                    return 'group-hover:scale-110 transition-all duration-300';
                }
              };
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    group flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors
                    ${active 
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-800'
                    }
                  `}
                >
                  <Icon className={`w-5 h-5 ${getIconAnimation(item.name)}`} />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Mobile menu button */}
        <div className="sticky top-16 z-30 lg:hidden bg-white/80 dark:bg-dark-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-dark-800 px-4 py-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
